import React from 'react';
import { v4 as uuid } from 'uuid';
import { Qualification } from '../../../models';
import { Button } from '../../commons';
import { ClearIcon } from '../../icons';
import { Input } from '../../inputs';

interface QualificationsFormProps {
  qualifications: Qualification[];
  setQualifications: React.Dispatch<React.SetStateAction<Qualification[]>>;
  isReadOnly?: boolean;
}

export default function QualificationsForm({
  qualifications,
  setQualifications,
  isReadOnly = false,
}: QualificationsFormProps) {
  const [name, setName] = React.useState('');
  const [type, setType] = React.useState('');
  const [institution, setInstitution] = React.useState('');
  const [duration, setDuration] = React.useState('');
  const [completed, setCompleted] = React.useState('');

  const add = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      setQualifications((_qualifications) => [
        ...(_qualifications ?? []),
        {
          id: uuid(),
          name,
          type,
          institution,
          duration,
          completed,
        },
      ]);
      setName('');
      setType('');
      setInstitution('');
      setDuration('');
      setCompleted('');
    },
    [completed, duration, institution, name, setQualifications, type]
  );

  const onDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (
      typeof window !== 'undefined' &&
      window.confirm('Are you sure you want to delete this qualification?')
    ) {
      const { id } = e.currentTarget.dataset;
      setQualifications((_qualifications) =>
        _qualifications.filter((q) => q.id !== id)
      );
    }
  };

  return (
    <div className="w-full">
      <p className="text-gray-500 text-[14px] font-bold mb-2">
        Kindly give details of your undergraduate qualifications including
        honours
      </p>
      {isReadOnly ? null : (
        <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-[1fr_1fr_2fr_1fr_0.5fr_0.5fr] gap-2 items-end">
          <Input
            type="text"
            label="Qualifications"
            name="qualification-name"
            value={name}
            onChange={(e) => {
              e.preventDefault();
              setName(e.target.value);
            }}
          />
          <Input
            type="text"
            label="Type of Award"
            name="qualification-type"
            value={type}
            onChange={(e) => {
              e.preventDefault();
              setType(e.target.value);
            }}
          />
          <Input
            type="text"
            label="Name and Location of Institution"
            name="qualification-institution"
            value={institution}
            onChange={(e) => {
              e.preventDefault();
              setInstitution(e.target.value);
            }}
          />
          <Input
            type="text"
            label="Duration of Study"
            name="qualification-duration"
            value={duration}
            onChange={(e) => {
              e.preventDefault();
              setDuration(e.target.value);
            }}
          />
          <Input
            type="text"
            label="Completed"
            name="qualification-completed"
            value={completed}
            onChange={(e) => {
              e.preventDefault();
              setCompleted(e.target.value);
            }}
          />
          <Button
            color="magenta"
            type="button"
            className="mt-auto mb-4"
            onClick={add}
          >
            Add
          </Button>
        </div>
      )}
      {qualifications?.length > 0 ? (
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr className="">
                <th className="px-2 align-middle border border-solid py-2 text-[12px] uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left text-neutral-600">
                  Qualifications
                </th>
                <th className="px-2 align-middle border border-solid py-2 text-[12px] uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left text-neutral-600">
                  Type of Award
                </th>
                <th className="px-2 align-middle border border-solid py-2 text-[12px] uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left text-neutral-600">
                  Name and Location of Institution
                </th>
                <th className="px-2 align-middle border border-solid py-2 text-[12px] uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left text-neutral-600">
                  Duration of Study
                </th>
                <th className="px-2 align-middle border border-solid py-2 text-[12px] uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left text-neutral-600">
                  Completed
                </th>
              </tr>
            </thead>
            <tbody>
              {qualifications?.map((qualification) => (
                <tr key={qualification.id}>
                  <td className="border-t-0 px-2 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-1 inline-flex">
                    {isReadOnly ? null : (
                      <button
                        type="button"
                        className="w-4 h-4 mr-1 my-auto text-red-600"
                        data-id={qualification.id}
                        onClick={onDelete}
                      >
                        <ClearIcon className="w-4 h-4" />
                      </button>
                    )}
                    <span className="my-auto">{qualification.name}</span>
                  </td>
                  <td className="border-t-0 px-2 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-1">
                    {qualification.type}
                  </td>
                  <td className="border-t-0 px-2 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-1">
                    {qualification.institution}
                  </td>
                  <td className="border-t-0 px-2 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-1">
                    {qualification.duration}
                  </td>
                  <td className="border-t-0 px-2 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-1">
                    {qualification.completed}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
